<template>
  <div v-if="tests.length > 0" class="my-4 w-full overflow-auto">
    <div class="mt-2 pb-2">
      <table
        class="table-auto min-w-full divide-y divide-gray-400 border-collapse rounded-lg"
        v-if="!smapEdit"
      >
        <thead class="bg-gray-50">
          <tr>
            <th class="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm</th>
            <th class="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtask ID</th>
            <th class="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lab Status</th>
            <th class="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent to Lab</th>
            <th class="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" v-if="['admin'].includes($page.props.auth.user.role)">Import</th>
            <th class="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            <th class="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certificate</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(test, index) in tests"
            :key="index"
            class="h-14 bg-white text-gray-900 text-sm"
          >
            <td class="px-1 py-2.5 cursor-pointer">
              <span v-on:click="selectFarm(test.farmer_id, test.farm_name)">
                {{ test.farm_name }}
              </span>
            </td>
            <td class="px-1 py-2.5 text-sm text-gray-600">
              {{ test.subtask_id }}
            </td>
            <td class="px-1 py-2.5">
              <span v-if="test.lab_status" class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    :class="{
                      'bg-yellow-100 text-yellow-800': ['Created', 'Unknown', 'Registered', 'Scheduled'].includes(test.lab_status),
                      'bg-blue-100 text-blue-800': test.lab_status === 'Testing',
                      'bg-green-100 text-green-800': ['Results', 'Invoice', 'Checked', 'Certified'].includes(test.lab_status)
                    }">
                {{ test.lab_status }}
              </span>
              <span v-else class="text-gray-400 text-xs">-</span>
            </td>
            <td class="px-1 py-2.5 text-sm text-gray-600">
              <span v-if="test.sent_to_lab_datetime">
                {{ test.sent_to_lab_datetime }}
                <span v-if="test.logsheet_emailed_at" class="block text-xs text-green-700">
                  Emailed to {{ test.lab_name }} {{ test.logsheet_emailed_at }}
                </span>
                <span v-else-if="test.logsheet_email_failed_at" class="block text-xs text-red-600">
                  Email to {{ test.lab_name }} failed {{ test.logsheet_email_failed_at }}
                </span>
              </span>
              <span v-else class="text-gray-400">Not sent</span>
            </td>
            <td
              class="px-1 py-2.5"
              v-if="['admin'].includes($page.props.auth.user.role)"
            >
              <button
                v-on:click="showEditSmap(test)"
                class="py-2 px-2 border text-xs rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
              >
                Import
              </button>
            </td>
            <td class="px-1 py-2.5">
              <div class="flex flex-wrap gap-1">
                <button
                  v-if="!test.path_issues"
                  v-on:click="goTo(route('app.task.create-csv', test.hashid))"
                  class="py-1 px-2 border text-xs rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
                  :disabled="task.status == 'collected'"
                >
                  Create CSV
                </button>
                <button
                  v-if="!test.path_issues"
                  v-on:click="goTo(route('app.task.view-csv', test.hashid))"
                  class="py-1 px-2 text-xs border rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
                  :disabled="!test.csv_created"
                >
                  Download CSV
                </button>
                <button
                  v-if="!test.path_issues && test.smap_smaple_paths"
                  v-on:click="goTo(route('app.task.send-to-lab', test.hashid))"
                  :class="getLabButtonClass(test)"
                  class="py-1 px-2 text-xs border rounded-md font-semibold transition ease-in-out duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  {{ getLabButtonText(test) }}
                </button>
                <button
                  v-if="test.code == 'DAFM' && ['admin'].includes($page.props.auth.user.role)"
                  v-on:click="goTo(route('app.task.send-to-isec', test.hashid))"
                  class="py-1 px-2 text-xs border rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
                >
                  Send to ISEC
                </button>
              </div>
            </td>
            <td class="px-1 py-2.5">
              <a
                v-if="getLabCertificate(test)"
                :href="route('app.task.download-fba-certificate', {
                  task: task.id,
                  fbaOrderHashid: getLabCertificate(test).fba_order_id
                })"
                @click="handleCertificateDownload($event, test)"
                class="inline-flex items-center py-1 px-2 text-xs border rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                title="Download FBA Lab Certificate"
              >
                <svg class="mr-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Certificate
              </a>
              <span v-else-if="isFbaTest(test)" class="text-gray-500 text-xs">Not Ready</span>
              <span v-else class="text-gray-400 text-xs">-</span>
            </td>
            <td v-if="test.path_issues">
              <div
                class="w-4 h-4 ml-2 text-center text-white text-xs rounded-full bg-red-600"
              >
                &nbsp;
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        class="bg-white text-gray-900 text-sm mt-2 pt-3 pb-2"
        v-if="smapEdit"
      >
        <h2 class="text-2xl font-bold mb-5 p-2 lg:mx-4">
          Editing {{ form.hashid }}
        </h2>
        <form @submit.prevent="updateSmap">
          <div class="w-full">
            <div class="p-2 lg:mx-4 lg:w-1/2">
              <label class="block text-sm font-medium text-gray-700" for="name">
                Hashid
              </label>

              <input
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                type="text"
                required
                disabled
                :value="form.hashid"
              />
            </div>

            <div class="p-2 lg:mx-4 lg:w-1/2">
              <label class="block text-sm font-medium text-gray-700" for="name">
                Name
              </label>

              <input
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                type="text"
                required
                disabled
                :value="form.farm_name"
              />
            </div>

            <div class="p-2 lg:mx-4 lg:w-1/2">
              <label class="block text-sm font-medium text-gray-700" for="name">
                Survey Ident
              </label>

              <input
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                type="text"
                required
                v-model="form.survey_ident"
              />

              <div class="text-red-600" v-if="form.errors?.survey_ident">
                {{ form.errors.survey_ident }}
              </div>
            </div>

            <div class="p-2 lg:mx-4 lg:w-1/2">
              <label class="block text-sm font-medium text-gray-700" for="name">
                UUID
              </label>

              <input
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                type="text"
                required
                v-model="form.update_id"
              />

              <div class="text-red-600" v-if="form.errors?.update_id">
                {{ form.errors.update_id }}
              </div>
            </div>
          </div>

          <div class="flex gap-x-3 justify-start p-2 lg:mx-4">
            <button
              type="submit"
              @click="form.reimport = false"
              class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Save
            </button>

            <button
              type="submit"
              @click="form.reimport = true"
              class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Save and reimport
            </button>

            <button
              type="submit"
              @click="smapEdit = false"
              class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <div v-if="tests.length === 0" class="bg-white px-6 py-4 my-4">
    <strong>No tests.</strong>
  </div>
</template>

<script>
export default {
  props: {
    tests: Object,
    task: Object,
    currentTab: String,
    labUpdateNeeded: Boolean,
    labUpdateChanges: Object,
    availableLabCertificates: {
      type: Array,
      default: () => []
    },
  },

  data() {
    return {
      smapEdit: false,
      form: {},
    };
  },

  methods: {
    selectFarm(index, farmer) {
      this.$root.search.farmer.selected = index;
      this.$root.search.farmer.search = farmer;

      let vm = this;

      axios.post(route("app.farmer.map.view", index)).then(function (response) {
        vm.$root.farmer = response.data;
      });
    },

    getLabButtonClass(test) {
      const hasUpdate = this.labUpdateNeeded && this.labUpdateChanges && this.labUpdateChanges.total_changes > 0;
      
      if (hasUpdate && test.sent_to_lab) {
        // Orange styling for updates
        return 'text-white bg-orange-600 hover:bg-orange-700 focus:ring-orange-500 border-transparent';
      } else {
        // Default blue styling
        return 'text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 border-transparent';
      }
    },

    getLabButtonText(test) {
      const hasUpdate = this.labUpdateNeeded && this.labUpdateChanges && this.labUpdateChanges.total_changes > 0;
      
      if (hasUpdate && test.sent_to_lab) {
        return 'Send Update';
      } else {
        return `Send to ${test.lab_name}`;
      }
    },

    goTo(url) {
      // If this is a send-to-lab URL, append the current tab parameter
      if (url.includes('send-to-lab')) {
        const currentTab = this.currentTab || 'actions';
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}from_tab=${currentTab}`;
      }
      
      // For CSV downloads, use window.location instead of Inertia to ensure proper file download
      if (url.includes('view-csv')) {
        window.location.href = url;
        return;
      }
      
      this.$inertia.visit(url);
    },
    showEditSmap(test) {
      this.smapEdit = true;
      this.form = test;
    },
    updateSmap() {
      let vm = this;
      axios
        .patch(route("app.task.smap.update", this.form.hashid), this.form)
        .then(function (response) {
          if (response.status === 200) {
            window.location.href = route("app.task.view", {
              task: vm.form.task_hashid,
              tab: "actions",
            });
          }
        });
    },

    getLabCertificate(test) {
      // Find the FBA certificate for this test
      return this.availableLabCertificates.find(cert => cert.test_id === test.hashid);
    },

    isFbaTest(test) {
      // Check if this is an FBA test by looking at lab status or lab name
      // FBA tests will have lab_status values like 'Created', 'Invoice', 'Certified', etc.
      return test.lab_status && 
             ['Created', 'Unknown', 'Registered', 'Scheduled', 'Testing', 'Results', 'Invoice', 'Checked', 'Certified'].includes(test.lab_status);
    },

    async handleCertificateDownload(event, test) {
      // Show immediate feedback to user
      const button = event.currentTarget;
      const originalText = button.innerHTML;
      
      // Change button to show downloading state
      button.innerHTML = `
        <svg class="mr-1 w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Downloading...
      `;
      button.disabled = true;
      
      try {
        // Use a simple window.location approach to trigger download
        // This will properly handle both successful downloads and error redirects
        window.location.href = button.href;
        
        // Reset button after a short delay
        setTimeout(() => {
          button.innerHTML = originalText;
          button.disabled = false;
        }, 3000);
        
      } catch (error) {
        // Reset button on error
        button.innerHTML = originalText;
        button.disabled = false;
        console.error('Certificate download error:', error);
      }
      
      // Prevent the original link from firing
      event.preventDefault();
      return false;
    },
  },
};
</script>
